import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { Deal, UserProfile } from '@bcc/shared';
import { DEAL_STATUSES } from '@bcc/shared';
import { Link } from 'react-router-dom';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD, SITE_GLASS_TILE } from '../constants/siteUi';
import { apiFetch } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export function AdminDashboard() {
  const { session } = useAuth();
  const token = session?.access_token;
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [analytics, setAnalytics] = useState<Record<string, unknown> | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);

  const [title, setTitle] = useState('Sun Belt Multifamily IV');
  const [description, setDescription] = useState(
    'Class B garden-style assets with light value-add in high-growth MSAs. Refer to PPM for full risk factors.'
  );
  const [irr, setIrr] = useState('14.5');
  const [equityMultiple, setEquityMultiple] = useState('1.9');
  const [min, setMin] = useState('25000');
  const [status, setStatus] = useState<(typeof DEAL_STATUSES)[keyof typeof DEAL_STATUSES]>(DEAL_STATUSES.OPEN);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      const [u, a, d] = await Promise.all([
        apiFetch<{ users: UserProfile[] }>('/api/users', { token }),
        apiFetch<Record<string, unknown>>('/api/admin/analytics', { token }),
        apiFetch<{ deals: Deal[] }>('/api/deals', { token }),
      ]);
      setUsers(u.users);
      setAnalytics(a);
      setDeals(d.deals);
    })();
  }, [token]);

  async function createDeal(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    await apiFetch('/api/deals', {
      method: 'POST',
      token,
      body: JSON.stringify({
        title,
        description,
        irr: Number(irr),
        equity_multiple: Number(equityMultiple),
        min_investment: Number(min),
        status,
        image_paths: [],
      }),
    });
    const d = await apiFetch<{ deals: Deal[] }>('/api/deals', { token });
    setDeals(d.deals);
  }

  async function removeDeal(id: string) {
    if (!token) return;
    if (!window.confirm('Delete this deal?')) return;
    await apiFetch(`/api/deals/${id}`, { method: 'DELETE', token });
    setDeals((rows) => rows.filter((x) => x.id !== id));
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-16 space-y-12">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-[var(--home-text-on-dark)]">Admin console</h1>
        <p className="mt-2 text-sm text-[var(--home-muted-on-dark)]">
          Role-gated surface for platform operators — changes are audited server-side.
        </p>
      </div>
      <aside className={`${SITE_GLASS_CARD} px-5 py-5 text-sm leading-relaxed`}>
        <p className="mb-1 font-semibold text-[var(--home-text-on-dark)]">CRM hub</p>
        <p className="text-xs text-[var(--home-muted-on-dark)]">
          Institutional relationships, sourcing corridors, counterpart progression, and deal attribution consolidate in
          CRM —{' '}
          <Link
            to="/admin/crm"
            className="font-medium text-[var(--home-miami-blue)] underline-offset-4 hover:text-[var(--home-miami-blue)]/90 hover:underline"
          >
            open CRM overview
          </Link>
          .
        </p>
      </aside>
      {analytics ? (
        <section className="grid sm:grid-cols-4 gap-4">
          {Object.entries(analytics).map(([k, v]) => (
            <div key={k} className={`${SITE_GLASS_TILE} p-4`}>
              <p className="text-[11px] uppercase tracking-wide text-[var(--home-muted-on-dark)]">{k}</p>
              <p className="mt-2 text-xl font-semibold tabular-nums text-[var(--home-text-on-dark)]">{String(v)}</p>
            </div>
          ))}
        </section>
      ) : null}
      <section className="grid lg:grid-cols-2 gap-10">
        <form className={`space-y-4 p-6 ${SITE_GLASS_CARD}`} onSubmit={createDeal}>
          <h2 className="text-sm font-semibold text-[var(--home-text-on-dark)]">Create deal</h2>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Description</span>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1 text-sm">
              <span className="text-[var(--home-muted-on-dark)]">IRR %</span>
              <input
                value={irr}
                onChange={(e) => setIrr(e.target.value)}
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="text-[var(--home-muted-on-dark)]">Equity multiple</span>
              <input
                value={equityMultiple}
                onChange={(e) => setEquityMultiple(e.target.value)}
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>
          </div>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Minimum investment ($)</span>
            <input
              type="number"
              value={min}
              onChange={(e) => setMin(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            >
              {Object.values(DEAL_STATUSES).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className="rounded-xl bg-[var(--home-miami-blue)] px-6 py-2.5 text-sm font-semibold text-[var(--home-zone-void)] shadow-md shadow-black/20 transition hover:brightness-105"
          >
            Publish deal
          </button>
        </form>
        <div className={`overflow-hidden ${SITE_GLASS_CARD}`}>
          <header className="border-b border-white/[0.08] px-6 py-4 text-sm font-semibold text-[var(--home-text-on-dark)]">
            Users snapshot
          </header>
          <table className="w-full text-xs">
            <thead className="bg-white/[0.06] text-left text-[10px] uppercase text-[var(--home-muted-on-dark)]">
              <tr>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Approved</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-white/[0.06]">
                  <td className="px-4 py-2 text-[var(--home-text-on-dark)]">{u.email}</td>
                  <td className="px-4 py-2 text-[var(--home-text-on-dark)]">{u.role}</td>
                  <td className="px-4 py-2 text-[var(--home-text-on-dark)]">
                    {u.investor_approved ? 'yes' : 'no'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className={`overflow-hidden ${SITE_GLASS_CARD}`}>
        <header className="border-b border-white/[0.08] px-6 py-4 text-sm font-semibold text-[var(--home-text-on-dark)]">
          Deals
        </header>
        <table className="w-full text-sm">
          <thead className="bg-white/[0.06] text-left text-xs uppercase text-[var(--home-muted-on-dark)]">
            <tr>
              <th className="px-6 py-3">Title</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Min</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody>
            {deals.map((d) => (
              <tr key={d.id} className="border-t border-white/[0.06]">
                <td className="px-6 py-3 text-[var(--home-text-on-dark)]">{d.title}</td>
                <td className="px-6 py-3 capitalize text-[var(--home-text-on-dark)]">{d.status}</td>
                <td className="px-6 py-3 text-[var(--home-text-on-dark)] tabular-nums">
                  ${Number(d.min_investment).toLocaleString()}
                </td>
                <td className="px-6 py-3 text-right">
                  <button
                    type="button"
                    className="text-xs text-red-400/95 hover:text-red-300"
                    onClick={() => void removeDeal(d.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
